import React, { useState, useCallback } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  alpha,
  Stack,
  Dialog,
  DialogContent,
  Button,
  Link,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  CompareArrows as CompareIcon,
  SearchOff as GapsIcon,
  Warning as WarningIcon,
  SmartToy as AgentIcon,
  Rule as RuleIcon,
  History as AuditIcon,
  Settings as SettingsIcon,
  BugReport as BugReportIcon,
  Logout,
  DesktopWindows as VncIcon,
  Language as WebIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  MenuBook as MenuBookIcon,
  EditCalendar as EditCalendarIcon,
  HelpOutline as HelpOutlineIcon,
  ContentCopy as ContentCopyIcon,
  CloudQueue as CloudQueueIcon,
  RateReview as RateReviewIcon,
  FactCheck as FactCheckIcon,
  Store as StoreIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useThemeMode } from "../../themeContext";
import { useCycleFilter, ALL_CYCLES } from "../../cycleFilterContext";
import { GuidedTour, resetAllTours } from "../tour/GuidedTour";
import { NotificationBell } from "../NotificationBell";
import { PostingRulesDialog } from "../PostingRulesDialog";
import { WhatsNewDialog } from "../WhatsNewDialog";
import { useGraphMe } from "../../auth/useGraphMe";

const DRAWER_WIDTH = 272;

const NAV_SECTIONS = [
  {
    label: "Reconciliation",
    items: [
      { path: "/", label: "Dashboard", icon: <DashboardIcon /> },
      { path: "/transactions", label: "Transactions", icon: <ReceiptIcon /> },
      { path: "/invoices", label: "Invoices", icon: <DescriptionIcon /> },
      { path: "/matches", label: "Match Review", icon: <CompareIcon /> },
      { path: "/review-queue", label: "Review Queue", icon: <RateReviewIcon /> },
      // W3 — route path kept as /pending-review for backwards compat, but
      // label renamed to "File Review" to disambiguate from the new SLA
      // watchdog surface on /transactions (posting review queue).
      { path: "/pending-review", label: "File Issues", icon: <EditCalendarIcon /> },
      { path: "/duplicates", label: "Duplicates", icon: <ContentCopyIcon /> },
      { path: "/gaps", label: "Invoice Gaps", icon: <GapsIcon /> },
      { path: "/transactions/trash", label: "Trash", icon: <DeleteSweepIcon /> },
      // /exceptions removed in v3.0.42 — the page surfaced
      // matchStatus="exception" rows but every legitimate one is now
      // covered by the Stage model on /transactions (chip NEEDS_INVOICE
      // for rows that need an invoice match) + /matches (the active
      // review surface). Page file kept in src/ for history; route
      // deleted from routes.tsx so the URL 404s if hit directly.
    ],
  },
  {
    label: "System",
    items: [
      { path: "/agents", label: "Agent Status", icon: <AgentIcon /> },
      { path: "/qb-queue", label: "QBD Queue", icon: <CloudQueueIcon /> },
      { path: "/pipeline-errors", label: "Pipeline Errors", icon: <BugReportIcon /> },
      { path: "/rules", label: "Rules Engine", icon: <RuleIcon /> },
      { path: "/audit", label: "Audit Trail", icon: <AuditIcon /> },
      { path: "/settings", label: "Settings", icon: <SettingsIcon /> },
    ],
  },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [forceRunTour, setForceRunTour] = useState(false);
  const handleStartTour = useCallback(() => {
    resetAllTours();
    setForceRunTour(true);
  }, []);
  const handleTourConsumed = useCallback(() => setForceRunTour(false), []);
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  // Resolve the account from BOTH sources. Teams SSO calls
  // `instance.setActiveAccount(...)` after ssoSilent succeeds; that
  // updates getActiveAccount() but `accounts[0]` can still lag for one
  // render. Prefer activeAccount when present, fall back to the first
  // cached account so refresh-on-mount keeps a meaningful name visible.
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  // Graph /me — last-resort identity source for tenants that strip
  // `name` and `preferred_username` from id_tokens (Altelix B2B / Teams
  // SSO). Silent only; returns null until the cache or fetch fills it.
  const graphMe = useGraphMe();
  // MSAL pulls multiple identity-bearing strings from id_token claims. Some
  // tenants populate `account.username` (UPN) but leave `account.name`
  // empty; some flip it; the Teams SSO path used here logs `name` while
  // leaving `username` blank on B2B guest accounts. Read every plausible
  // source so we never end up with literally nothing to show.
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    graphMe?.mail ||
    graphMe?.userPrincipalName ||
    "";
  // For the role lookup we don't trust a single email source — Azure AD
  // commonly exposes a principal under multiple forms (UPN on
  // `*.onmicrosoft.com`, SMTP alias on the custom domain, etc.) and the
  // Convex `users` row is keyed by exactly one of them. Pass every
  // plausible alias to `findByAnyEmail`; it tries each one in order.
  const roleEmailCandidates = [
    account?.username ?? "",
    claimStr("preferred_username"),
    claimStr("upn"),
    claimStr("email"),
    graphMe?.mail ?? "",
    graphMe?.userPrincipalName ?? "",
  ].filter((e) => e && e.includes("@"));
  const userRecord = useQuery(
    api.users.findByAnyEmail,
    roleEmailCandidates.length > 0 ? { emails: roleEmailCandidates } : "skip",
  );
  const role = userRecord?.role ?? "viewer";
  const isAdmin = role === "admin";
  // Posting-rules questionnaire: highlight the menu item until a signed
  // submission exists. Query may be undefined on first render / pre-deploy.
  const hasRulesSubmission = useQuery(api.postingRules.hasSubmission, {});

  // Display-name cascade. Pre-fix the sidebar showed the literal "User"
  // whenever MSAL returned an account without a `name` claim, and after
  // the 2026-05-15 first attempt was still landing on the "Account"
  // fallback for guest/SSO accounts because `account.username` came back
  // empty too. Broaden the cascade to read every reasonable id_token
  // source:
  //   1. Convex users.name (manually curated — wins)
  //   2. MSAL account.name
  //   3. claims.name / given_name+family_name
  //   4. Derived from the resolved email's local-part
  //   5. The raw email
  //   6. "Account" — last resort
  const prettyFromEmail = (e: string) => {
    const local = e.split("@")[0] ?? "";
    const pretty = local
      .replace(/[._-]+/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(" ");
    return pretty || e;
  };
  const claimGiven = claimStr("given_name");
  const claimFamily = claimStr("family_name");
  const fromGivenFamily =
    claimGiven || claimFamily ? `${claimGiven} ${claimFamily}`.trim() : "";
  const graphGivenFamily =
    graphMe && (graphMe.givenName || graphMe.surname)
      ? `${graphMe.givenName} ${graphMe.surname}`.trim()
      : "";
  const displayName: string =
    (userRecord?.name && userRecord.name.trim()) ||
    (graphMe?.displayName && graphMe.displayName.trim()) ||
    (account?.name && account.name.trim()) ||
    claimStr("name") ||
    fromGivenFamily ||
    graphGivenFamily ||
    (email ? prettyFromEmail(email) : "") ||
    "Account";
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "?";

  // Global cycle filter — pulled from CycleFilterProvider. Driven by the
  // dropdown in the top bar; consumed by every page's Convex queries.
  const { selectedCycleId, setSelectedCycleId, isAll } = useCycleFilter();
  const cycleList = useQuery(api.reconciliationCycles.listForSelector, {});
  const visibleCycles = (cycleList ?? []).filter((c: any) => c.status !== "archived");

  // Note (2026-05-13): the sidebar nav badge chips were removed at the
  // operator's request — the multi-signal `/transactions` badge in
  // particular (cycling between transactionsCount / pendingPostingReview
  // / driftCount with different colors) was confusing more than it
  // helped. Each destination page renders its own count in its header,
  // which is the canonical place. The `api.navBadges` query is no longer
  // called from this component; `pendingReviewCount`, `pendingPostingReview`,
  // `driftCount`, `navBadgeCount`, `navBadgeColor` and friends have been
  // removed alongside the JSX that used them.

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Brand Header */}
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            component="img"
            src="/altelix-logo.png"
            alt="Altelix"
            sx={{
              height: 42,
              width: "auto",
              objectFit: "contain",
              flexShrink: 0,
              filter: "var(--c-logo-filter)",
            }}
          />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: "0.9rem",
                background: "linear-gradient(135deg, var(--c-brand-title-start) 30%, var(--c-link-accent))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1.2,
              }}
            >
              Squad Finance
            </Typography>
            <Typography variant="caption" sx={{ color: "var(--c-text-muted)", fontSize: "0.6rem", letterSpacing: "0.5px" }}>
              Altelix Reconciliation
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: "var(--c-overlay-4)", mx: 2 }} />

      {/* Navigation Sections */}
      <Box sx={{ flex: 1, overflow: "auto", py: 1.5 }}>
        {NAV_SECTIONS.map((section) => (
          <Box key={section.label} sx={{ mb: 1 }} data-tour={`nav-${section.label.toLowerCase()}`}>
            <Typography
              variant="caption"
              sx={{
                px: 3,
                py: 1,
                display: "block",
                fontSize: "0.6rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "2px",
                color: "var(--c-text-muted)",
              }}
            >
              {section.label}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: "0.8125rem" }}
                  />
                  {/*
                    Sidebar count chips removed (2026-05-13): the per-route
                    badge counts were misleading operators because some
                    (`/transactions`) cycled between transactionsCount,
                    pendingPostingReview, and driftCount with different
                    semantics (and palette) depending on which was non-zero
                    on the active cycle. Each destination page already
                    shows its own accurate count in its header — that's
                    the canonical place for numbers.
                  */}
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}

        {/* Posting Rules questionnaire — visible to everyone; highlighted
            with an "Action needed" chip until a signed submission exists. */}
        <Box sx={{ mb: 1 }}>
          <List disablePadding>
            <ListItemButton onClick={() => { setRulesOpen(true); setMobileOpen(false); }}>
              <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}><FactCheckIcon /></ListItemIcon>
              <ListItemText primary="Posting Rules" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              {hasRulesSubmission === false && (
                <Chip
                  label="Action needed"
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700 }}
                />
              )}
            </ListItemButton>
          </List>
        </Box>

        {isAdmin && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                px: 3,
                py: 1,
                display: "block",
                fontSize: "0.6rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "2px",
                color: "var(--c-text-muted)",
              }}
            >
              Administration
            </Typography>
            <List disablePadding>
              <ListItemButton
                selected={location.pathname === "/admin/vendors"}
                onClick={() => { navigate("/admin/vendors"); setMobileOpen(false); }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}><StoreIcon /></ListItemIcon>
                <ListItemText primary="Vendors" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              </ListItemButton>
              <ListItemButton
                selected={location.pathname === "/admin/gl-accounts"}
                onClick={() => { navigate("/admin/gl-accounts"); setMobileOpen(false); }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}><AccountBalanceIcon /></ListItemIcon>
                <ListItemText primary="GL Accounts" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              </ListItemButton>
              <ListItemButton
                selected={location.pathname === "/admin/cards"}
                onClick={() => { navigate("/admin/cards"); setMobileOpen(false); }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}><CreditCardIcon /></ListItemIcon>
                <ListItemText primary="Cards" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              </ListItemButton>
              <ListItemButton
                selected={location.pathname === "/admin/posting-rules"}
                onClick={() => { navigate("/admin/posting-rules"); setMobileOpen(false); }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}><FactCheckIcon /></ListItemIcon>
                <ListItemText primary="Rule Submissions" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              </ListItemButton>
              <ListItemButton
                onClick={() => window.open("https://vnc-altelix.4clouders.com", "_blank")}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}><VncIcon /></ListItemIcon>
                <ListItemText
                  primary="OpenClaw Console"
                  primaryTypographyProps={{ fontSize: "0.8125rem" }}
                />
              </ListItemButton>
            </List>
          </Box>
        )}
      </Box>

      {/* User Footer */}
      <Box
        sx={{
          p: 1.5,
          mx: 1,
          mb: 1,
          borderRadius: 2.5,
          background: "var(--c-overlay-2)",
          border: "1px solid var(--c-overlay-4)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {/* Avatar with initials — derived from the resolved display name,
              so it stays meaningful even when MSAL doesn't return `account.name`. */}
          <Box
            aria-hidden
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              color: "#fff",
              background: isAdmin
                ? "linear-gradient(135deg, #6c5ce7, #a29bfe)"
                : "linear-gradient(135deg, #00cec9, #55efc4)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            }}
          >
            {initials}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={displayName} disableInteractive placement="top">
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.2 }}
                noWrap
              >
                {displayName}
              </Typography>
            </Tooltip>
            {/* Secondary line: email when available, otherwise a role-aware
                hint. "Not signed in" only fires when literally no MSAL
                account is present (logged-out / before SSO completes). */}
            <Tooltip
              title={email || (account ? "Active session — no email claim" : "Not signed in")}
              disableInteractive
              placement="bottom"
            >
              <Typography
                variant="caption"
                sx={{ color: "var(--c-text-muted)", fontSize: "0.68rem" }}
                noWrap
              >
                {email || (account ? "Active session" : "Not signed in")}
              </Typography>
            </Tooltip>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Chip
              label={role.charAt(0).toUpperCase() + role.slice(1)}
              size="small"
              color={isAdmin ? "primary" : "default"}
              variant={isAdmin ? "filled" : "outlined"}
              sx={{ height: 20, fontSize: "0.62rem", fontWeight: 600, px: 0.25 }}
            />
            <IconButton
              onClick={() => instance.logoutRedirect()}
              size="small"
              aria-label="Sign out"
              sx={{
                color: "var(--c-text-muted)",
                "&:hover": { color: "#ff7675", background: "rgba(255,118,117,0.08)" },
              }}
            >
              <Logout sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Start Tour button */}
      <Box sx={{ px: 2, pb: 0.5 }}>
        <Button
          size="small"
          startIcon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
          onClick={handleStartTour}
          fullWidth
          sx={{
            textTransform: "none",
            fontSize: "0.7rem",
            color: "var(--c-text-muted)",
            justifyContent: "center",
            py: 0.5,
            borderRadius: 1.5,
            "&:hover": {
              background: "var(--c-overlay-4)",
              color: "var(--c-link-accent)",
            },
          }}
        >
          Start Guided Tour
        </Button>
      </Box>

      {/* 4Clouders Footer */}
      <Box
        sx={{
          px: 2.5,
          pb: 1.5,
          pt: 0.5,
          textAlign: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "var(--c-text-muted)",
            fontSize: "0.6rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          Developed with
          <Box component="span" sx={{ color: "#ff7675", fontSize: "0.7rem", lineHeight: 1 }}>❤️</Box>
          by
          <Link
            href="https://4clouders.com"
            target="_blank"
            rel="noopener"
            sx={{
              color: "var(--c-link-accent)",
              textDecoration: "none",
              fontWeight: 700,
              "&:hover": { color: "#6c5ce7" },
            }}
          >
            4Clouders
          </Link>
        </Typography>
        <Typography
          component="span"
          onClick={() => setAboutOpen(true)}
          sx={{
            color: "var(--c-text-faint)",
            fontSize: "0.55rem",
            cursor: "pointer",
            "&:hover": { color: "var(--c-link-accent)" },
            transition: "color 0.2s",
          }}
        >
          About this project
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "var(--c-bg-base)" }}>
      {/* Top Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ minHeight: "56px !important" }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { sm: "none" }, color: "var(--c-text-secondary)" }}
          >
            <MenuIcon />
          </IconButton>
          {/* Top bar title — shows current page name with the product as a
              subtle prefix. Pre-fix the title was the static product name
              regardless of where the user was, which wasted the most
              prominent piece of real estate. The label resolves from the
              same NAV_SECTIONS array that drives the sidebar so it stays
              in sync when nav items are renamed. */}
          {(() => {
            // WR-04: admin routes are not in NAV_SECTIONS (they render separately
            // and conditionally in the sidebar). Extend allItems with admin paths
            // when the user is an admin so the top-bar title resolves correctly
            // for /admin/vendors, /admin/gl-accounts, /admin/cards.
            const adminItems = isAdmin ? [
              { path: "/admin/vendors", label: "Vendors" },
              { path: "/admin/gl-accounts", label: "GL Accounts" },
              { path: "/admin/cards", label: "Cards" },
            ] : [];
            const allItems = [...NAV_SECTIONS.flatMap((s) => s.items), ...adminItems];
            const current = allItems.find((i) => i.path === location.pathname);
            const pageLabel = current?.label ?? "";
            return (
              <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    color: "var(--c-text-muted)",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    display: { xs: "none", md: "inline" },
                  }}
                >
                  Squad Finance
                </Typography>
                {pageLabel && (
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      color: "var(--c-text-faint)",
                      fontSize: "0.7rem",
                      display: { xs: "none", md: "inline" },
                    }}
                  >
                    /
                  </Box>
                )}
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{ fontWeight: 700, color: "var(--c-text-primary)" }}
                >
                  {pageLabel || "Altelix Financial Reconciliation"}
                </Typography>
              </Stack>
            );
          })()}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ ml: "auto" }}>
            {/* Global cycle filter (Option B / multi-active cycles).
                Default = "All Cycles" → no filter applied. */}
            <FormControl size="small" sx={{ minWidth: 200 }} data-tour="cycle-selector">
              <Select
                value={selectedCycleId}
                onChange={(e) => setSelectedCycleId(e.target.value as string)}
                displayEmpty
                aria-label="Filter by reconciliation cycle"
                sx={{
                  borderRadius: 2,
                  fontSize: "0.8rem",
                  height: 36,
                  background: "var(--c-overlay-2)",
                  "& .MuiSelect-select": { py: 0.75, display: "flex", alignItems: "center", gap: 1 },
                  "& fieldset": { borderColor: "var(--c-border)" },
                  "&:hover fieldset": { borderColor: "var(--c-link-accent) !important" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 420,
                      "& .MuiMenuItem-root": { fontSize: "0.8rem" },
                    },
                  },
                }}
              >
                <MenuItem value={ALL_CYCLES}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                    <Box sx={{ flex: 1, fontWeight: 600 }}>All Cycles</Box>
                    <Chip
                      label="default"
                      size="small"
                      sx={{ height: 18, fontSize: "0.6rem", background: "var(--c-overlay-4)", color: "var(--c-text-muted)" }}
                    />
                  </Stack>
                </MenuItem>
                {visibleCycles.length > 0 && <Divider sx={{ my: 0.5 }} />}
                {visibleCycles.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                      <Box sx={{ flex: 1 }}>{c.name}</Box>
                      <Chip
                        label={c.status}
                        size="small"
                        color={c.status === "active" ? "primary" : c.status === "completed" ? "success" : "default"}
                        sx={{ height: 18, fontSize: "0.6rem" }}
                      />
                      {c.totalTransactions > 0 && (
                        <Typography variant="caption" sx={{ color: "var(--c-text-muted)" }}>
                          {c.reconciliationRate}%
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Start guided tour">
              <IconButton
                onClick={handleStartTour}
                size="small"
                aria-label="Start guided tour"
                sx={{
                  color: "var(--c-text-secondary)",
                  border: "1px solid var(--c-border)",
                  borderRadius: 2,
                  width: 34,
                  height: 34,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "var(--c-link-accent)",
                    borderColor: "var(--c-link-accent)",
                    background: "var(--c-overlay-4)",
                  },
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <NotificationBell userEmail={email} />
            <Tooltip title="Open user manual">
              <IconButton
                onClick={() => window.open("/manual/Squad-Finance-User-Manual.html", "_blank", "noopener,noreferrer")}
                size="small"
                aria-label="Open user manual"
                sx={{
                  color: "var(--c-text-secondary)",
                  border: "1px solid var(--c-border)",
                  borderRadius: 2,
                  width: 34,
                  height: 34,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "var(--c-link-accent)",
                    borderColor: "var(--c-link-accent)",
                    background: "var(--c-overlay-4)",
                  },
                }}
              >
                <MenuBookIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={mode === "light" ? "Switch to dark mode" : "Switch to light mode"}>
              <IconButton
                onClick={toggleMode}
                size="small"
                aria-label="Toggle color theme"
                sx={{
                  color: "var(--c-text-secondary)",
                  border: "1px solid var(--c-border)",
                  borderRadius: 2,
                  width: 34,
                  height: 34,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "var(--c-link-accent)",
                    borderColor: "var(--c-link-accent)",
                    background: "var(--c-overlay-4)",
                  },
                }}
              >
                {mode === "light" ? <DarkModeIcon sx={{ fontSize: 18 }} /> : <LightModeIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Chip
              label="AI-Powered"
              size="small"
              sx={{
                height: 22,
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.5px",
                background: "rgba(0,206,201,0.1)",
                color: "var(--c-metric-success)",
                border: "1px solid rgba(0,206,201,0.2)",
              }}
            />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: 0 }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: "56px",
          minHeight: "calc(100vh - 56px)",
          bgcolor: "var(--c-bg-base)",
        }}
      >
        {children}
      </Box>

      {/* What's New — release notes shown once per release (scroll-to-ack) */}
      <WhatsNewDialog />

      {/* Guided Tour */}
      <GuidedTour forceRun={forceRunTour} onForceRunConsumed={handleTourConsumed} />

      {/* Posting Rules questionnaire (in-app, submits to Convex) */}
      <PostingRulesDialog
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        submittedByEmail={email}
      />

      {/* About Modal */}
      <Dialog
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(12px)",
              backgroundColor: "rgba(8,12,22,0.7)",
            },
          },
        }}
        PaperProps={{
          sx: {
            background: "var(--c-bg-elevated)",
            border: "1px solid rgba(108,92,231,0.2)",
            borderRadius: 4,
            overflow: "hidden",
          },
        }}
      >
        {/* Gradient accent bar */}
        <Box
          sx={{
            height: 3,
            background: "linear-gradient(90deg, #6c5ce7, #00cec9, #a29bfe)",
          }}
        />
        <DialogContent sx={{ p: 0 }}>
          <IconButton
            onClick={() => setAboutOpen(false)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "var(--c-text-muted)",
              "&:hover": { color: "var(--c-text-primary)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ px: 4, pt: 4, pb: 1, textAlign: "center" }}>
            {/* Altelix Logo */}
            <Box
              component="img"
              src="/altelix-logo.png"
              alt="Altelix"
              sx={{
                height: 80,
                width: "auto",
                objectFit: "contain",
                display: "inline-block",
                mb: 2,
                filter: "var(--c-logo-filter)",
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                background: "linear-gradient(135deg, var(--c-brand-title-start) 30%, var(--c-link-accent))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 0.5,
              }}
            >
              Squad Finance
            </Typography>
            <Typography variant="body2" sx={{ color: "var(--c-text-secondary)", mb: 3 }}>
              AI-Powered Financial Reconciliation Platform
            </Typography>
          </Box>

          {/* Info Cards */}
          <Box sx={{ px: 4, pb: 3 }}>
            <Stack spacing={1.5}>
              {[
                { label: "Client", value: "Altelix, LLC — Boca Raton, FL" },
                { label: "Platform", value: "5 AI Agents orchestrated by OpenClaw" },
                { label: "Stack", value: "React + Convex + MUI + Claude Opus" },
                { label: "Version", value: "v3.0 — QuickBooks Desktop Integration" },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    background: "var(--c-overlay-2)",
                    border: "1px solid var(--c-overlay-4)",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "var(--c-text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.6rem" }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--c-text-primary)", fontSize: "0.8rem", fontWeight: 500 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Buttons */}
          <Box sx={{ px: 4, pb: 4 }}>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<WebIcon />}
                onClick={() => window.open("https://4clouders.com", "_blank")}
                fullWidth
                sx={{
                  background: "linear-gradient(135deg, #6c5ce7, #4834d4)",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  py: 1.2,
                  "&:hover": {
                    background: "linear-gradient(135deg, #7c6cf7, #5844e4)",
                  },
                }}
              >
                4Clouders.com
              </Button>
              <Button
                variant="outlined"
                startIcon={<InfoIcon />}
                onClick={() => window.open("https://github.com/TiagoMCF/PROJECT001-SQUAD-FINANCE", "_blank")}
                fullWidth
                sx={{
                  borderColor: "rgba(108,92,231,0.3)",
                  color: "var(--c-link-accent)",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  py: 1.2,
                  "&:hover": {
                    borderColor: "rgba(108,92,231,0.6)",
                    background: "rgba(108,92,231,0.08)",
                  },
                }}
              >
                Project Repo
              </Button>
            </Stack>
          </Box>

          {/* Bottom branding */}
          <Box
            sx={{
              textAlign: "center",
              py: 2,
              borderTop: "1px solid var(--c-overlay-4)",
              background: "rgba(0,0,0,0.15)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "var(--c-text-faint)",
                fontSize: "0.6rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              Developed with
              <Box component="span" sx={{ color: "#ff7675", fontSize: "0.7rem", lineHeight: 1 }}>❤️</Box>
              by
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  background: "linear-gradient(90deg, #6c5ce7, #00cec9)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                4Clouders Cloud Intelligence
              </Box>
            </Typography>
            <Typography variant="caption" sx={{ color: "var(--c-text-faintest)", fontSize: "0.5rem" }}>
              © {new Date().getFullYear()} All rights reserved
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
